import type { PropertyValueResource } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
export function useProjectTenantVariableValueChangeTracker(): ProjectTenantVariableValueChangeTracker {
    const [changes, setChanges] = useState<Map<ProjectTenantVariableValueKey, PropertyValueResource | undefined>>(new Map());
    return {
        has: (key: ProjectTenantVariableValueKey) => changes.has(key),
        get: (key: ProjectTenantVariableValueKey) => {
            if (changes.has(key)) {
                return changes.get(key);
            }
            throw new Error(`Key "${key}" not found in changed project tenant variables`);
        },
        set: (key: ProjectTenantVariableValueKey, value: PropertyValueResource | undefined) => {
            const copy = new Map(changes);
            copy.set(key, value);
            setChanges(copy);
        },
        remove: (key: ProjectTenantVariableValueKey) => {
            const copy = new Map(changes);
            copy.delete(key);
            setChanges(copy);
        },
        reset: () => setChanges(new Map()),
        values: () => Array.from(changes.entries()).map(([key, value]) => {
            const { projectId, templateId, tenantId, environmentId } = extractProjectTenantVariableIds(key);
            return {
                projectId,
                templateId,
                tenantId,
                environmentId,
                value,
            };
        }),
        hasAny: () => changes.size > 0,
        changedTenants: () => {
            const tenantIds: Set<string> = new Set();
            for (const variableKey of changes.keys()) {
                const { tenantId } = extractProjectTenantVariableIds(variableKey);
                tenantIds.add(tenantId);
            }
            return tenantIds;
        },
    };
}
export interface ProjectTenantVariableValueChangeTracker {
    has: (key: ProjectTenantVariableValueKey) => boolean;
    get: (key: ProjectTenantVariableValueKey) => PropertyValueResource | undefined; // To satisfy type check. Notice that PropertyValueResource can be undefined
    set: (key: ProjectTenantVariableValueKey, value: PropertyValueResource | undefined) => void;
    remove: (key: ProjectTenantVariableValueKey) => void;
    reset: () => void;
    values: () => ChangedProjectTemplateVariableValue[];
    hasAny: () => boolean;
    changedTenants: () => Set<string>;
}
export interface ChangedProjectTemplateVariableValue {
    projectId: string;
    templateId: string;
    tenantId: string;
    environmentId: string;
    value: PropertyValueResource | undefined;
}
export type ProjectTenantVariableValueKey = `${string};${string};${string};${string}`;
export function createProjectTenantVariableKey(projectId: string, templateId: string, tenantId: string, environmentId: string): ProjectTenantVariableValueKey {
    return `${projectId};${templateId};${tenantId};${environmentId}`;
}
function extractProjectTenantVariableIds(key: ProjectTenantVariableValueKey): {
    projectId: string;
    templateId: string;
    tenantId: string;
    environmentId: string;
} {
    const extracted = key.split(`;`);
    return {
        projectId: extracted[0],
        templateId: extracted[1],
        tenantId: extracted[2],
        environmentId: extracted[3],
    };
}
